import { 
  Type, 
  Hash, 
  ToggleLeft, 
  Braces, 
  Brackets, 
  Ban,
  HelpCircle,
  Calendar,
  Link as LinkIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';

export type DataType = 
  | 'string' 
  | 'number' 
  | 'boolean' 
  | 'object' 
  | 'array' 
  | 'null' 
  | 'undefined'
  | 'date'
  | 'url'
  | 'unknown';

interface DataTypeIconProps {
  type: DataType;
  className?: string;
  size?: number;
}

const typeConfig: Record<DataType, { icon: any; color: string; label: string }> = {
  string: {
    icon: Type,
    color: 'text-blue-600 dark:text-blue-400',
    label: 'String'
  },
  number: {
    icon: Hash,
    color: 'text-purple-600 dark:text-purple-400',
    label: 'Number'
  },
  boolean: {
    icon: ToggleLeft,
    color: 'text-green-600 dark:text-green-400',
    label: 'Boolean'
  },
  object: {
    icon: Braces,
    color: 'text-orange-600 dark:text-orange-400',
    label: 'Object'
  },
  array: {
    icon: Brackets,
    color: 'text-pink-600 dark:text-pink-400',
    label: 'Array'
  },
  null: {
    icon: Ban,
    color: 'text-gray-400 dark:text-gray-500',
    label: 'Null'
  },
  undefined: {
    icon: Ban,
    color: 'text-gray-400 dark:text-gray-500',
    label: 'Undefined'
  },
  date: {
    icon: Calendar,
    color: 'text-cyan-600 dark:text-cyan-400',
    label: 'Date'
  },
  url: {
    icon: LinkIcon,
    color: 'text-indigo-600 dark:text-indigo-400',
    label: 'URL'
  },
  unknown: {
    icon: HelpCircle,
    color: 'text-gray-400 dark:text-gray-500',
    label: 'Unknown'
  }
};

/**
 * Detects the data type of a value with enhanced type detection
 */
export function detectDataType(value: any): DataType {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  
  const type = typeof value;
  
  if (type === 'string') {
    // Check if it's a URL
    try {
      new URL(value);
      return 'url';
    } catch {
      // Check if it's a date string
      if (!isNaN(Date.parse(value)) && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return 'date';
      }
    }
    return 'string';
  }
  
  if (type === 'number') return 'number';
  if (type === 'boolean') return 'boolean';
  
  if (Array.isArray(value)) return 'array';
  
  if (value instanceof Date) return 'date';
  
  if (type === 'object') return 'object';
  
  return 'unknown';
}

/**
 * DataTypeIcon component displays an icon representing the data type
 */
export function DataTypeIcon({ type, className, size = 14 }: DataTypeIconProps) {
  const config = typeConfig[type];
  const Icon = config.icon;
  
  return (
    <Icon 
      className={cn(config.color, className)} 
      size={size}
      aria-label={config.label}
    />
  );
}

/**
 * Get the color class for a data type
 */
export function getDataTypeColor(type: DataType): string {
  return typeConfig[type]?.color || typeConfig.unknown.color;
}

/**
 * Get the label for a data type
 */
export function getDataTypeLabel(type: DataType): string {
  return typeConfig[type]?.label || typeConfig.unknown.label;
}
